/**
 * Keyboard navigation utilities for handling page-level shortcuts
 * without interfering with interactive components (menus, dialogs, etc.)
 */

/** Same-tick guard so Escape that closed a popup is not also a page shortcut. */
let overlayEscapeUntil = 0;

export function markOverlayHandledEscape() {
  overlayEscapeUntil = Date.now() + 80;
}

export function isRecentOverlayEscape() {
  return Date.now() < overlayEscapeUntil;
}

/** Test helper: drop the same-tick Esc guard. */
export function resetOverlayHandledEscape() {
  overlayEscapeUntil = 0;
}

function elementTag(el) {
  return el?.tagName ? String(el.tagName).toUpperCase() : "";
}

/** True when the key event is aimed at a text field or editable area. */
export function isTypingInField(e) {
  const target = e?.target;
  const active = typeof document !== "undefined" ? document.activeElement : null;
  const isFieldTag = (el) => ["INPUT", "TEXTAREA", "SELECT"].includes(elementTag(el));
  if (isFieldTag(target) || isFieldTag(active)) return true;
  if (target?.isContentEditable || active?.isContentEditable) return true;
  return false;
}

/**
 * Check if a keyboard event should be handled by page-level shortcuts.
 * Returns false if focus is inside an interactive component that should
 * capture keyboard events (menus, dialogs, selects, etc.)
 * 
 * @param {KeyboardEvent} e - The keyboard event
 * @param {Object} options - Optional configuration
 * @param {boolean} options.allowInButtons - If true, allow handling even when button is focused (default: false)
 * @returns {boolean} - True if the page should handle this keyboard event
 */
export function shouldHandlePageKeyboard(e, options = {}) {
  const target = e.target;
  const { allowInButtons = false } = options;
  const active = typeof document !== "undefined" ? document.activeElement : null;

  // Dialogs (and similar) already handled this key. Do not also run page shortcuts
  // such as Escape leaving Edit deal after a calculator closes.
  if (e.defaultPrevented) {
    return false;
  }

  if (e.key === "Escape" && Date.now() < overlayEscapeUntil) {
    return false;
  }

  // Don't handle if typing in input fields — check both target and activeElement
  // (keydown target can occasionally disagree with focus during IME / delegation edge cases)
  if (isTypingInField(e)) {
    return false;
  }

  // Overlay closed in the same keydown (node already unmounted). The leftover
  // event must not be treated as a page shortcut.
  if (target instanceof Element && !target.isConnected) {
    return false;
  }
  
  // Don't handle if focus is inside a Radix UI interactive component
  // These components have their own keyboard navigation
  const interactiveSelectors = [
    '[role="menu"]',
    '[role="menuitem"]',
    '[role="listbox"]',
    '[role="option"]',
    '[role="dialog"]',
    '[role="alertdialog"]',
    '[role="combobox"]',
    '[role="tree"]',
    '[role="treeitem"]',
    '[data-radix-menu-content]',
    '[data-radix-select-content]',
    '[data-radix-popover-content]',
    '[data-radix-dialog-content]',
    '[data-radix-alert-dialog-content]',
    '[data-radix-dropdown-menu-content]',
    '[data-radix-context-menu-content]',
  ].join(', ');

  const closestInteractive = (el) =>
    el instanceof Element ? el.closest(interactiveSelectors) : null;

  if (closestInteractive(target) || closestInteractive(active)) {
    return false;
  }

  if (e.key === "Escape" && isAnyDialogOpen()) {
    return false;
  }
  
  // For Enter and Space keys, don't interfere with buttons and links
  // Let them trigger their native click behavior
  if (!allowInButtons && ['Enter', ' '].includes(e.key)) {
    if (['BUTTON', 'A'].includes(target.tagName)) {
      return false;
    }
    // Also check for elements with role="button"
    if (target.getAttribute('role') === 'button') {
      return false;
    }
  }
  
  return true;
}

/**
 * Check if a dialog or modal is currently open in the DOM.
 * Useful as an additional check for page-level handlers.
 * 
 * @returns {boolean} - True if any dialog/modal appears to be open
 */
export function isAnyDialogOpen() {
  // Check for Radix dialog portals
  const radixDialogs = document.querySelectorAll('[data-radix-dialog-content], [data-radix-alert-dialog-content]');
  if (radixDialogs.length > 0) {
    return true;
  }
  
  // Check for elements with dialog role that are visible
  const dialogs = document.querySelectorAll('[role="dialog"], [role="alertdialog"]');
  for (const dialog of dialogs) {
    const style = window.getComputedStyle(dialog);
    if (style.display !== 'none' && style.visibility !== 'hidden') {
      return true;
    }
  }
  
  return false;
}

/**
 * Check if a menu or dropdown is currently open.
 * 
 * @returns {boolean} - True if any menu appears to be open
 */
export function isAnyMenuOpen() {
  const menus = document.querySelectorAll('[role="menu"], [data-radix-menu-content], [data-radix-dropdown-menu-content]');
  return menus.length > 0;
}

const FOCUS_OVERLAY_SELECTORS = [
  '[role="dialog"]',
  '[role="alertdialog"]',
  '[role="menu"]',
  '[role="listbox"]',
  "[data-radix-dialog-content]",
  "[data-radix-alert-dialog-content]",
  "[data-radix-menu-content]",
  "[data-radix-select-content]",
  "[data-radix-popover-content]",
  "[data-radix-dropdown-menu-content]",
  "[data-radix-context-menu-content]",
].join(", ");

function isInsideFocusOverlay(el) {
  return el instanceof Element && Boolean(el.closest(FOCUS_OVERLAY_SELECTORS));
}

function isFloatingOverlayOpen() {
  if (typeof document === "undefined") return false;
  return Boolean(
    document.querySelector(
      "[data-radix-select-content], [data-radix-popover-content], [data-radix-dropdown-menu-content]"
    )
  );
}

/** True when this node is a real focused control, not the page background. */
export function isPageFocusTarget(el) {
  if (typeof document === "undefined") return false;
  if (!el || el === document.body || el === document.documentElement) return false;
  return typeof el.blur === "function";
}

/** Drop focus the same way a click on empty page background would. */
export function clearPageFocus(el) {
  const target =
    el || (typeof document !== "undefined" ? document.activeElement : null);
  if (!isPageFocusTarget(target)) return false;
  target.blur();
  return true;
}

/**
 * Esc can unfocus after windows, menus, and page-specific Esc steps have had
 * a chance to run (those handlers preventDefault). Same idea as clicking outside.
 */
export function shouldClearPageFocusOnEscape(e) {
  if (!e || e.key !== "Escape") return false;
  if (e.repeat || e.isComposing) return false;
  if (e.metaKey || e.ctrlKey || e.altKey) return false;
  if (e.defaultPrevented) return false;
  if (isRecentOverlayEscape()) return false;
  if (isAnyDialogOpen() || isAnyMenuOpen() || isFloatingOverlayOpen()) return false;
  const target = e.target;
  if (target instanceof Element && !target.isConnected) return false;
  const active = typeof document !== "undefined" ? document.activeElement : null;
  if (isInsideFocusOverlay(target) || isInsideFocusOverlay(active)) return false;
  const focusEl = isPageFocusTarget(active) ? active : target;
  return isPageFocusTarget(focusEl);
}
